import { useState, useEffect, useMemo, useRef } from 'react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function endToLabel(end) {
  const d = new Date(end + 'T12:00:00Z');
  const q = Math.ceil((d.getUTCMonth() + 1) / 3);
  return `Q${q}'${String(d.getUTCFullYear()).slice(2)}`;
}

function fmtBig(val) {
  if (val == null) return '—';
  const sign = val < 0 ? '-' : '';
  const abs  = Math.abs(val);
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9)  return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6)  return `${sign}$${(abs / 1e6).toFixed(0)}M`;
  return `${sign}$${abs.toFixed(2)}`;
}

// Get { q, y, key } from an end-date string
function getQuarterYear(end) {
  const d = new Date(end + 'T12:00:00Z');
  const q = Math.ceil((d.getUTCMonth() + 1) / 3);
  const y = d.getUTCFullYear();
  return { q, y, key: `${y}-Q${q}` };
}

// YoY % using the formula: ((current - prior) / |prior|) * 100
function yoy(current, prior) {
  if (current == null || prior == null || prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function EarningsPanel({ ticker, centerDate }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [tableView, setTableView] = useState('annual'); // 'annual' | 'quarterly'

  // Track what's already loaded so we don't re-fetch unnecessarily
  const loadedTicker = useRef(null);
  const loadedMinMs  = useRef(null); // earliest revenue end-date in loaded data (ms)

  useEffect(() => {
    if (!ticker) return;

    const cdMs = centerDate ? new Date(centerDate.slice(0, 10)).getTime() : null;

    // Same ticker — skip if loaded data already covers this centerDate
    if (loadedTicker.current === ticker && loadedMinMs.current !== null) {
      const coveredFrom = loadedMinMs.current - 95 * 86400e3;
      if (cdMs === null || cdMs >= coveredFrom) return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    loadedMinMs.current = null;

    const qs = centerDate ? `?centerDate=${centerDate.slice(0, 10)}` : '';
    fetch(`/api/edgar/facts/${ticker}${qs}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        const rev = d.revenue || [];
        loadedTicker.current = ticker;
        loadedMinMs.current  = rev.length ? new Date(rev[0].end).getTime() : null;
        setData(d);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [ticker, centerDate]);

  // ── Annual stats table data (YoY vs prior full year) ──────────────────────
  const annualStatsData = useMemo(() => {
    if (!data) return [];
    const yearMap = {};
    const addToYear = (arr, field) => {
      (arr || []).forEach(q => {
        const y = new Date(q.end + 'T12:00:00Z').getUTCFullYear();
        if (!yearMap[y]) yearMap[y] = {};
        if (!yearMap[y][field]) yearMap[y][field] = [];
        yearMap[y][field].push(q.value);
      });
    };
    addToYear(data.revenue,    'rev');
    addToYear(data.epsDiluted, 'eps');
    addToYear(data.netIncome,  'ni');
    const sum = arr => arr?.reduce((a, b) => a + b, 0) ?? null;
    const sortedYears = Object.keys(yearMap).map(Number).sort();
    return sortedYears.map((year, i) => {
      const g         = yearMap[year];
      const rev       = sum(g.rev);
      const eps       = sum(g.eps);
      const ni        = sum(g.ni);
      const netMargin = rev && ni != null ? (ni / rev) * 100 : null;
      let revYoy = null, epsYoy = null, marginYoy = null;
      if (i > 0) {
        const pg      = yearMap[sortedYears[i - 1]];
        const pRev    = sum(pg.rev);
        const pEps    = sum(pg.eps);
        const pNi     = sum(pg.ni);
        const pMargin = pRev && pNi != null ? (pNi / pRev) * 100 : null;
        revYoy    = yoy(rev, pRev);
        epsYoy    = yoy(eps, pEps);
        marginYoy = yoy(netMargin, pMargin);
      }
      return { year, rev, eps, netMargin, revYoy, epsYoy, marginYoy };
    });
  }, [data]);

  // ── Quarterly stats table data (YoY vs same quarter prior year) ────────────
  const quarterlyStatsData = useMemo(() => {
    if (!data) return [];
    const epsQMap = {}, revQMap = {}, niQMap = {};
    (data.epsDiluted || []).forEach(q => { epsQMap[getQuarterYear(q.end).key] = q.value; });
    (data.revenue    || []).forEach(q => { revQMap[getQuarterYear(q.end).key] = q.value; });
    (data.netIncome  || []).forEach(q => { niQMap[getQuarterYear(q.end).key]  = q.value; });
    const sorted = [...(data.revenue || [])].sort((a, b) => a.end.localeCompare(b.end));
    const window = sorted;
    return window.map(item => {
      const { q, y, key } = getQuarterYear(item.end);
      const prevKey    = `${y - 1}-Q${q}`;
      const eps        = epsQMap[key]     ?? null;
      const rev        = revQMap[key]     ?? null;
      const ni         = niQMap[key]      ?? null;
      const pEps       = epsQMap[prevKey] ?? null;
      const pRev       = revQMap[prevKey] ?? null;
      const pNi        = niQMap[prevKey]  ?? null;
      const netMargin  = rev  && ni  != null ? (ni  / rev)  * 100 : null;
      const pNetMargin = pRev && pNi != null ? (pNi / pRev) * 100 : null;
      return {
        label:     endToLabel(item.end),
        eps,       epsYoy:    yoy(eps, pEps),
        rev,       revYoy:    yoy(rev, pRev),
        netMargin, marginYoy: yoy(netMargin, pNetMargin),
      };
    });
  }, [data]);

  const hasAnyData = annualStatsData.length > 0 || quarterlyStatsData.length > 0;

  // ── States ──────────────────────────────────────────────────────────────────
  if (!ticker) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#8b949e', fontSize: 11 }}>
      Click a holding to view fundamentals
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 10 }}>
      <div style={{ width: 20, height: 20, border: '2px solid #21262d', borderTop: '2px solid #58a6ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <span style={{ color: '#8b949e', fontSize: 11 }}>Fetching SEC EDGAR filings for {ticker}…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: 24, color: '#FF6B6B', fontSize: 11 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>EDGAR lookup failed for {ticker}</div>
      <div style={{ color: '#8b949e' }}>{error}</div>
      <div style={{ marginTop: 8, color: '#8b949e' }}>
        This may be an ETF, foreign issuer, or unrecognised ticker symbol.
      </div>
    </div>
  );

  if (!hasAnyData) return (
    <div style={{ padding: 24, color: '#8b949e', fontSize: 11 }}>
      No XBRL quarterly data found for {ticker}.
      {data?.entityName && <span style={{ color: '#e6edf3' }}> ({data.entityName})</span>}
      <div style={{ marginTop: 6 }}>ETFs and foreign private issuers typically do not file 10-Q reports.</div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Entity name from EDGAR */}
      {data?.entityName && (
        <div style={{ fontSize: 10, color: '#58a6ff', marginBottom: 10, fontWeight: 600 }}>
          {data.entityName}
          <span style={{ color: '#8b949e', fontWeight: 400, marginLeft: 6 }}>
            CIK {data.cik} · SEC EDGAR
          </span>
        </div>
      )}

      {/* Toggle */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {['annual', 'quarterly'].map(v => (
          <button key={v} onClick={() => setTableView(v)} style={{
            fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px',
            padding: '3px 8px', borderRadius: 4, cursor: 'pointer', border: 'none',
            background: tableView === v ? '#21262d' : 'transparent',
            color: tableView === v ? '#e6edf3' : '#8b949e',
          }}>
            {v === 'annual' ? 'Annual' : 'Quarterly'}
          </button>
        ))}
      </div>

      {/* ── Version 1: Annual vertical ── */}
      {tableView === 'annual' && annualStatsData.length > 0 && (() => {
        const yoyCell = (val) => (
          <td style={{ padding: '3px 8px', textAlign: 'right', fontSize: 10,
            color: val == null ? '#8b949e' : val >= 0 ? '#58a6ff' : '#FF6B6B' }}>
            {val != null ? `${val >= 0 ? '+' : ''}${val.toFixed(2)}%` : '-'}
          </td>
        );
        const sectionHeader = (cols) => (
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={i} style={{ padding: '2px 8px', textAlign: i === 0 ? 'left' : 'right',
                  color: '#8b949e', fontSize: 9, fontWeight: 600, borderBottom: '1px solid #21262d' }}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
        );
        const rowStyle = { borderBottom: '1px solid #161b22' };
        const cellBase = { padding: '3px 8px', fontSize: 10, color: '#e6edf3' };
        const colStyle = {
          overflowY: 'auto',
          maxHeight: 280,
        };
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {/* EPS */}
            <div>
              <div style={{ fontSize: 9, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.4px', marginBottom: 4 }}>Annual Earnings</div>
              <div style={colStyle}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  {sectionHeader(['Year', 'EPS', 'EPS Growth'])}
                  <tbody>
                    {annualStatsData.map(r => (
                      <tr key={r.year} style={rowStyle}>
                        <td style={{ ...cellBase, textAlign: 'left' }}>{r.year}</td>
                        <td style={{ ...cellBase, textAlign: 'right' }}>
                          {r.eps != null ? `$${r.eps.toFixed(2)}` : '—'}
                        </td>
                        {yoyCell(r.epsYoy)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Sales */}
            <div>
              <div style={{ fontSize: 9, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.4px', marginBottom: 4 }}>Annual Sales</div>
              <div style={colStyle}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  {sectionHeader(['Year', 'Sales', 'Sales Growth'])}
                  <tbody>
                    {annualStatsData.map(r => (
                      <tr key={r.year} style={rowStyle}>
                        <td style={{ ...cellBase, textAlign: 'left' }}>{r.year}</td>
                        <td style={{ ...cellBase, textAlign: 'right' }}>{fmtBig(r.rev)}</td>
                        {yoyCell(r.revYoy)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* After-tax net margin */}
            <div>
              <div style={{ fontSize: 9, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.4px', marginBottom: 4 }}>After-Tax Net Margin</div>
              <div style={colStyle}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  {sectionHeader(['Year', 'Margin', 'Growth'])}
                  <tbody>
                    {annualStatsData.map(r => (
                      <tr key={r.year} style={rowStyle}>
                        <td style={{ ...cellBase, textAlign: 'left' }}>{r.year}</td>
                        <td style={{ ...cellBase, textAlign: 'right' }}>
                          {r.netMargin != null ? `${r.netMargin.toFixed(1)}%` : '—'}
                        </td>
                        {yoyCell(r.marginYoy)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Version 2: Quarterly horizontal ── */}
      {tableView === 'quarterly' && quarterlyStatsData.length > 0 && (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr>
                <th style={{ padding: '3px 8px', textAlign: 'left', color: '#8b949e', fontSize: 9,
                  fontWeight: 600, borderBottom: '1px solid #21262d', minWidth: 80 }}>
                  Stats Table
                </th>
                {quarterlyStatsData.map(q => (
                  <th key={q.label} style={{ padding: '3px 8px', textAlign: 'center',
                    color: '#8b949e', fontSize: 9, fontWeight: 600, borderBottom: '1px solid #21262d',
                    minWidth: 64 }}>
                    {q.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { rowLabel: 'Earnings (EPS)', valFn: q => q.eps != null ? `$${q.eps.toFixed(2)}` : '—', yoyFn: q => q.epsYoy },
                { rowLabel: 'Sales',          valFn: q => fmtBig(q.rev),                               yoyFn: q => q.revYoy },
                { rowLabel: 'Net Margin',      valFn: q => q.netMargin != null ? `${q.netMargin.toFixed(1)}%` : '—', yoyFn: q => q.marginYoy },
              ].map(({ rowLabel, valFn, yoyFn }) => (
                <tr key={rowLabel} style={{ borderBottom: '1px solid #161b22' }}>
                  <td style={{ padding: '4px 8px', color: '#e6edf3', fontWeight: 600, fontSize: 10 }}>
                    {rowLabel}
                  </td>
                  {quarterlyStatsData.map(q => {
                    const v = yoyFn(q);
                    return (
                      <td key={q.label} style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <div style={{ color: '#e6edf3', fontSize: 10 }}>{valFn(q)}</div>
                        <div style={{ fontSize: 9, marginTop: 1,
                          color: v == null ? '#8b949e' : v >= 0 ? '#58a6ff' : '#FF6B6B' }}>
                          {v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
